import type { Settings } from "../features/settings/types";

function systemPrefersDark(): boolean {
  return !!window.matchMedia?.("(prefers-color-scheme: dark)").matches;
}

export function resolveThemeMode(mode: Settings["themeMode"]): "light" | "dark" {
  if (mode === "light") return "light";
  if (mode === "dark") return "dark";
  return systemPrefersDark() ? "dark" : "light";
}

export function applySettingsToDom(s: Settings) {
  const root = document.documentElement;

  // language
  root.lang = s.language || "ru";

  // theme
  const theme = resolveThemeMode(s.themeMode);
  root.dataset.theme = theme;

  // ui style
  root.dataset.ui = s.uiStyle || "standard";

  // custom glass color - apply to entire site (custom or glass)
  if ((s.uiStyle || "standard").startsWith("liquidGlass") && s.customGlassColor) {
    const rgb = hexToRgb(s.customGlassColor);
    root.style.setProperty("--glass-accent-custom", s.customGlassColor);
    root.style.setProperty("--glass-accent-custom-rgb", rgb);
    
    // Apply custom color effects to cards and backgrounds
    root.style.setProperty("--primary", `rgba(${rgb}, 0.9)`);
    root.style.setProperty("--primarySoft", `rgba(${rgb}, 0.12)`);
    
    // Update glass variables for custom mode
    root.style.setProperty("--glass-border", `rgba(${rgb}, 0.22)`);
  } else {
    root.style.removeProperty("--glass-accent-custom");
    root.style.removeProperty("--glass-accent-custom-rgb");
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primarySoft");
    root.style.removeProperty("--glass-border");
  }

  // motion
  root.dataset.motion = s.reduceMotion ? "reduce" : "full";

  // handy body hooks
  document.body.classList.toggle("is-glass", (s.uiStyle || "standard").startsWith("liquidGlass"));
  document.body.classList.toggle("is-glass-custom", (s.uiStyle || "standard") === "liquidGlassCustom");
}

/**
 * Convert hex color to RGB values
 * @param hex - Color in hex format (#RRGGBB)
 * @returns CSS rgb() string or comma-separated values for use with rgba()
 */
function hexToRgb(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return "107, 163, 255"; // fallback blue
  const r = parseInt(result[1], 16);
  const g = parseInt(result[2], 16);
  const b = parseInt(result[3], 16);
  return `${r}, ${g}, ${b}`;
}
